<script setup>
import { computed } from 'vue';

import { HrTextField } from '../primitives';

/*
 * inheritAttrs: false because the mask below is an attribute rather than a declared prop. With
 * inheritance left on, the fallthrough is applied after the template's own bindings, so a caller
 * passing `mask` would replace the one the seconds conversion depends on. $attrs is bound
 * explicitly, before the mask, so class, aria-* and listeners still reach the field.
 */

/**
 * Duration field — collects a length of time as HH:MM:SS and emits it as a number of seconds.
 *
 * The caller works in seconds, which is how durations are stored, and never sees the display
 * format or the input masking that produces it.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Collecting a duration that is stored as a number of seconds
 * @avoidWhen Collecting a time of day — use HrTimeField, which emits a Timestamp
 * @related HrTimeField
 * @a11y An incomplete entry stays incomplete rather than being silently completed, so a partly
 * typed duration is never announced or submitted as a value the user did not choose.
 */
defineOptions({ name: 'HrDurationField', inheritAttrs: false });

const props = defineProps({
  /**
   * Field style.
   * @values filled, outlined
   */
  variant: {
    type: String,
    default: 'outlined',
    validator: (v) => ['filled', 'outlined'].includes(v),
  },
  /**
   * Duration in seconds, or null when empty. Displayed as HH:MM:SS, so a value the two-digit hour
   * cannot hold — negative, or 100 hours and over — shows as an empty field rather than as a
   * different duration.
   * @example 5400
   */
  modelValue: {
    type: Number,
    default: null,
  },
  /** Label text shown floating above the field. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text below the field: one sentence the reader needs to fill it in. Defaults to
   * the expected format.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  hint: {
    type: String,
    default: 'HH:MM:SS',
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Marks the field invalid. */
  error: {
    type: Boolean,
    default: false,
  },
  /** Message shown when `error` is set. */
  errorMessage: {
    type: String,
    default: null,
  },
  /**
   * Validation rules, each receiving the duration in seconds rather than the displayed string, so
   * a rule reads as a comparison instead of string parsing.
   */
  rules: {
    type: Array,
    default: null,
  },
  /** Defers validation until blur. */
  lazyRules: {
    type: Boolean,
    default: false,
  },
  /** Removes the reserved space below the field for validation messages. */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /** Reduces the field height. */
  dense: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Emitted on change; payload is the duration in seconds, or null when the field is cleared. */
  'update:modelValue': null,
});

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * 60;

const MAX_SECONDS = 100 * SECONDS_PER_HOUR - 1;

/*
 * Anything the mask cannot render round-trips wrong, so the field shows nothing rather than a
 * different duration: 360000 seconds would print as 100:00:00 and come straight back through the
 * two-digit hour as 10:00:00. Negative and non-finite go the same way — a duration is neither, and
 * Math.abs would turn a negative one into a positive the caller never held.
 */
function toDisplay(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0 || seconds > MAX_SECONDS) {
    return '';
  }

  const whole = Math.floor(seconds);
  const hours = Math.floor(whole / SECONDS_PER_HOUR);
  const minutes = Math.floor((whole % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const secs = whole % SECONDS_PER_MINUTE;

  return [hours, minutes, secs].map((part) => String(part).padStart(2, '0')).join(':');
}

/*
 * An incomplete entry is null, not a partial duration. Reporting 0 for a half-typed value would
 * make an empty field look like a deliberate zero to a required-field rule.
 */
function toSeconds(display) {
  const parts = String(display ?? '').split(':');
  if (parts.length !== 3 || parts.some((part) => part === '')) {
    return null;
  }

  const [hours, minutes, seconds] = parts.map(Number);
  if ([hours, minutes, seconds].some(Number.isNaN)) {
    return null;
  }

  /*
   * Out of range is incomplete, not a duration to normalise. Accepting 00:99:99 would emit 6039
   * seconds, which comes back through the mask as 01:40:39: the field would silently rewrite what
   * the user typed. The hour bound is the mask's own, two digits wide.
   */
  const isInRange = hours >= 0 && hours <= 99
    && minutes >= 0 && minutes <= 59
    && seconds >= 0 && seconds <= 59;

  if (!isInRange) {
    return null;
  }

  return hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
}

const displayValue = computed(() => toDisplay(props.modelValue));

// Rules are authored against seconds, so wrap each one to convert before it runs.
const secondsRules = computed(() => props.rules?.map(
  (rule) => (value) => rule(toSeconds(value)),
) ?? null);

function onUpdate(value) {
  emit('update:modelValue', toSeconds(value));
}
</script>

<!--
  The mask is Quasar's DSL and stays here deliberately. It reaches HrTextField as an attribute
  rather than a declared prop, which keeps a vendor format string out of the design system's
  public surface: callers of this component work in seconds and never see it.

  Two digits for the hours. The filter dialog this replaces used a single-digit hour, so it could
  not express a duration of ten hours or more at all. No fill-mask either: filling an empty field
  with zeroes renders "no value" as 00:00:00, which reads as a deliberate zero.
-->
<template>
  <hr-text-field
    v-bind="$attrs"
    :variant="variant"
    :model-value="displayValue"
    :label="label"
    :hint="hint"
    :disable="disable"
    :error="error"
    :error-message="errorMessage"
    :rules="secondsRules"
    :lazy-rules="lazyRules"
    :hide-bottom-space="hideBottomSpace"
    :dense="dense"
    mask="##:##:##"
    @update:model-value="onUpdate"
  />
</template>
